import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { School, Book, CalendarIcon, Heart, Image, Store, Lightbulb, Database, BookOpen, BookText, FolderKanban, ClipboardList, Search, TrendingUp, SearchCheck, DollarSign, BarChart3, FileText, Users, Sun, Settings, Music } from 'lucide-react';
import { useAdminAppAccess } from '@/hooks/useAdminAppAccess';
import { getAllowedEmails } from '@/services/allowedEmails';

interface AppAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEmails: string[];
}

const appNameMap = {
  'good-things': 'Good Thing Tracker',
  'plms': 'JLS PLMS',
  'sop': 'SOP Central',
  'dayaway': 'DayAway Board',
  'ideogram': 'Ideogram PromptStorm',
  'inventory': 'Listing Management System',
  'pmt2': 'PMT2',
  'idea-machine': 'The Idea Machine',
  'ai-devquest': 'AI DevQuest',
  'glossary': 'Glossary',
  'employee-directory': 'Employee Directory',
  'product-manager': 'JLS Product Manager',
  'ppc-farm': 'PPC Farm Mail Tracker',
  'sb-search-term-report': 'SB Search Term Report',
  'farming-results': 'Farming Results',
  'amazon-index-checker': 'Amazon Index Checker',
  'primary-ledger': 'Primary Ledger',
  'sales-and-profit-history': 'Sales & Profit History',
  'days-of-cover': 'Days of Cover',
  'smart-writer': 'Smart Writer',
  // Productivity Apps
  'planova': 'Planova',
  'quickship': 'QuickShip',
  'whiteboard': 'Whiteboard',
  'checkin': 'CheckIn',
  // Community Apps
  'under-one-sky': 'Under One Sky',
  'pet-connect': 'Pet Connect',
  'fadr': 'Fadr',
} as const;

const appIconsConfig = {
  'good-things': { icon: Heart, color: 'bg-pink-500', description: 'Track and celebrate team achievements and recognition' },
  'plms': { icon: School, color: 'bg-emerald-500', description: 'Track and manage your personal learning journey' },
  'sop': { icon: Book, color: 'bg-green-500', description: 'Access and manage Standard Operating Procedures' },
  'dayaway': { icon: CalendarIcon, color: 'bg-cyan-500', description: 'Plan and celebrate team rest and recovery time' },
  'ideogram': { icon: Image, color: 'bg-indigo-500', description: 'Create and manage AI-generated images' },
  'inventory': { icon: Store, color: 'bg-blue-500', description: 'Optimize and manage product listings' },
  'pmt2': { icon: Database, color: 'bg-orange-500', description: 'Monitor and report on Amazon seller data' },
  'ai-devquest': { icon: BookOpen, color: 'bg-gray-700', description: 'Coding with AI Ecourse - Learn AI development skills' },
  'idea-machine': { icon: Lightbulb, color: 'bg-purple-500', description: 'Generate and track creative ideas systematically' },
  'glossary': { icon: BookText, color: 'bg-teal-500', description: 'Access and manage company terminology and definitions' },
  'employee-directory': {
    icon: () => (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
      >
        <circle cx="10" cy="7" r="4"></circle>
        <path d="M10.3 15H7a4 4 0 0 0-4 4v2"></path>
        <circle cx="17" cy="17" r="3"></circle>
        <path d="m21 21-1.9-1.9"></path>
      </svg>
    ),
    color: 'bg-blue-600',
    description: 'Company employee database and directory management'
  },
  'product-manager': {
    icon: FolderKanban,
    color: 'bg-violet-600',
    description: 'Collaborative project and task management for JLS Apps development'
  },
  'ppc-farm': {
    icon: ClipboardList,
    color: 'bg-yellow-500',
    description: 'A tool to keep track of direct mail campaigns from PPC Farm.'
  },
  'sb-search-term-report': {
    icon: Search,
    color: 'bg-purple-600',
    description: 'Analyze Amazon Sponsored Brands search terms and performance'
  },
  'farming-results': {
    icon: TrendingUp,
    color: 'bg-green-600',
    description: 'Track and analyze farming campaign results and metrics'
  },
  'amazon-index-checker': {
    icon: SearchCheck,
    color: 'bg-amber-600',
    description: 'Check if your product is indexed for specific search terms on Amazon'
  },
  'primary-ledger': {
    icon: DollarSign,
    color: 'bg-slate-600',
    description: 'Track and analyze primary financial metrics and performance data'
  },
  'sales-and-profit-history': {
    icon: BarChart3,
    color: 'bg-violet-500',
    description: 'Analyze product-level sales performance and profit margins over time'
  },
  'days-of-cover': {
    icon: () => (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
      >
        <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
      </svg>
    ),
    color: 'bg-emerald-600',
    description: 'Monitor inventory coverage and identify potential stockouts'
  },
  'smart-writer': {
    icon: FileText,
    color: 'bg-purple-600',
    description: 'AI-powered content generation with multiple models and revision capabilities'
  },
  // Productivity Apps
  'planova': {
    icon: BarChart3,
    color: 'bg-red-500',
    description: 'Visual project timelines, kanban boards, and real-time insights'
  },
  'quickship': {
    icon: Settings,
    color: 'bg-green-500',
    description: 'Automated project provisioning for Firebase, GitHub, and Netlify'
  },
  'whiteboard': {
    icon: Image,
    color: 'bg-violet-500',
    description: 'Interactive collaborative whiteboard for team brainstorming'
  },
  'checkin': {
    icon: Users,
    color: 'bg-cyan-500',
    description: 'Share how you\'re arriving today and feel closer to your team'
  },
  // Community Apps
  'under-one-sky': {
    icon: Sun,
    color: 'bg-blue-500',
    description: 'Real-time weather tracking, smart alerts, and collaborative analytics'
  },
  'pet-connect': {
    icon: Heart,
    color: 'bg-amber-500',
    description: 'Share your pet\'s special moments with your team'
  },
  'fadr': {
    icon: Music,
    color: 'bg-pink-500',
    description: 'Your team\'s shared soundtrack, powered by AI personality and real-time vibes'
  },
} as const;

const appIcons = Object.entries(appIconsConfig).map(([id, config]) => ({
  id,
  ...config,
  name: appNameMap[id as keyof typeof appNameMap]
}));

export const AppAccessDialog: React.FC<AppAccessDialogProps> = ({
  open,
  onOpenChange,
  selectedEmails = [],
}) => {
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [displayNames, setDisplayNames] = React.useState<{[key: string]: string}>({});
  const { userAppAccess, updateBulkUserAppAccess } = useAdminAppAccess();

  // Fetch display names when dialog opens
  React.useEffect(() => {
    if (open) {
      getAllowedEmails().then(emails => {
        const nameMap = emails.reduce((acc, { email, displayName }) => ({
          ...acc,
          [email.toLowerCase()]: displayName || email
        }), {});
        setDisplayNames(nameMap);
      });
    }
  }, [open]);

  const getAppAccessState = (appId: string) => {
    if (selectedEmails.length === 1) {
      const access = userAppAccess.find(a => 
        a.email.toLowerCase() === selectedEmails[0].toLowerCase()
      );
      return access?.allowedApps?.includes(appId) || false;
    }
    
    const accessStates = selectedEmails.map(email => {
      const access = userAppAccess.find(a => 
        a.email.toLowerCase() === email.toLowerCase()
      );
      return access?.allowedApps?.includes(appId) || false;
    });
    
    const allHaveAccess = accessStates.every(Boolean);
    const someHaveAccess = accessStates.some(Boolean);
    
    return allHaveAccess ? true : someHaveAccess ? 'indeterminate' : false;
  };

  const handleToggleApp = async (appId: string, hasAccess: boolean) => {
    setIsUpdating(true);
    try {
      await updateBulkUserAppAccess(selectedEmails, appId, hasAccess);
    } catch (error) {
      console.error('Error updating app access:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const getDialogTitle = () => {
    if (selectedEmails.length === 1) {
      const email = selectedEmails[0];
      const displayName = displayNames[email.toLowerCase()];
      return `App Access for ${displayName} (${email})`;
    }
    return `Manage App Access (${selectedEmails.length} users selected)`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl dark:bg-gray-800 dark:border-gray-700">
        <DialogHeader>
          <DialogTitle className="dark:text-white">{getDialogTitle()}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] overflow-y-auto pr-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4">
            {appIcons.map(({ id, icon: Icon, color, name, description }) => (
              <div
                key={id}
                className="flex items-start space-x-4 p-4 rounded-lg border hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className={`p-2 ${color} rounded-lg`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium dark:text-white">{name}</h4>
                    <Switch
                      checked={getAppAccessState(id) === true}
                      onCheckedChange={(checked: boolean) => handleToggleApp(id, checked)}
                      disabled={isUpdating}
                    />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
